import { useTranslation } from "react-i18next"

type Translations = typeof defaultTranslations

type UseTranslationsParams = {
  filters?: string
  projectCount?: number
  managerCount?: number
  completedProjectCount?: number
  onTimeProjectCount?: number
  lateProjectCount?: number
  noScheduleProjectCount?: number
  pastStartProjectCount?: number
  pastEndProjectCount?: number
}

const defaultTranslations = {
  upcomingProjectsSectionHeader: "Upcoming projects",
  pieChartCardHeader: "{{count}} projects in total",
  pieChartCardHeaderForOneProject: "1 project in total",
  managersPieChartCardHeader: "{{count}} project managers in {{projectCount}} projects",
  managersPieChartCardHeaderForOneProject: "{{count}} project managers in 1 projects",
  managersPieChartCardHeaderForOneProjectWithOneManager: "1 project manager in 1 project",
  managersPieChartCardHeaderForOneProjectWithMultipleManagers: "1 project managers in {{projectCount}} project",
  managersLabel: "Managers (projects)",
  manualCompletionBarTitle: "Projects manual completion overview",
  taskBasedCompletionBarTitle: "Projects task-based completion overview",
  appliedFiltersLabel: "Applied filters: {{filters}}",

  completedProjectsSectionHeader: "Completed projects",
  fromDatedPickerLabel: "Completed after",
  toDatedPickerLabel: "Completed before",
  unitLabel: "Unit",
  monthLabel: "Month",
  weekLabel: "Week",
  dayLabel: "Day",
  numberOfProjectsLabel: "Number of projects",
  monthlyScaleLabel: "Monthly scale",
  weeklyScaleLabel: "Weekly scale",
  dailyScaleLabel: "Daily scale",
  completedBarCardHeader: "{{count}} completed projects",
  completedBarCardHeaderForOneProject: "1 completed project",
  scheduleLabel: "Schedule",
  onTimeProjectsLabel: "{{count}} projects on time",
  onTimeProjectsLabelForOneProject: "1 project on time",
  lateProjectsLabel: "{{count}} projects late",
  lateProjectsLabelForOneProject: "1 project late",
  noScheduleProjectsLabel: "{{count}} projects without schedule",
  noScheduleProjectsLabelForOneProject: "1 project without schedule",
  pastStartProjectsLabel: "{{count}} projects past start",
  pastStartProjectsLabelForOneProject: "1 project past start",
  pastEndProjectsLabel: "{{count}} projects past end",
  pastEndProjectsLabelForOneProject: "1 project past end",

  customersFilterLabel: "Filter by customers",
  membershipsFilterLabel: "Filter by members",
  suppliersFilterLabel: "Filter by suppliers",
  workspacesFilterLabel: "Filter by workspaces",

  noData: "No data",
  notStarted: "Not started",
  inProgress: "In progress",
  completed: "Completed",
  rejected: "Rejected",
}

export const useTranslations = ({
  filters = "",
  projectCount = 0,
  managerCount = 0,
  completedProjectCount = 0,
  onTimeProjectCount = 0,
  lateProjectCount = 0,
  noScheduleProjectCount = 0,
  pastStartProjectCount = 0,
  pastEndProjectCount = 0,
}: UseTranslationsParams = {}): Translations => {
  const { t: tProject } = useTranslation("project")
  const { t: tTreegrid } = useTranslation("treegrid")

  return {
    upcomingProjectsSectionHeader: tProject("projectsDashboard.upcomingProjectsSectionHeader", {
      defaultValue: defaultTranslations.upcomingProjectsSectionHeader,
    }),
    pieChartCardHeader:
      projectCount === 1
        ? tProject("projectsDashboard.pieChartCardHeaderForOneProject", {
            defaultValue: defaultTranslations.pieChartCardHeaderForOneProject,
          })
        : tProject("projectsDashboard.pieChartCardHeader", {
            defaultValue: defaultTranslations.pieChartCardHeader,
            count: projectCount,
          }),
    pieChartCardHeaderForOneProject: tProject("projectsDashboard.pieChartCardHeaderForOneProject", {
      defaultValue: defaultTranslations.pieChartCardHeaderForOneProject,
    }),
    managersPieChartCardHeader:
      projectCount === 1 && managerCount === 1
        ? tProject("projectsDashboard.managersPieChartCardHeaderForOneProjectWithOneManager", {
            defaultValue: defaultTranslations.managersPieChartCardHeaderForOneProjectWithOneManager,
          })
        : projectCount === 1
        ? tProject("projectsDashboard.managersPieChartCardHeaderForOneProject", {
            defaultValue: defaultTranslations.managersPieChartCardHeaderForOneProject,
            count: managerCount,
          })
        : managerCount === 1
        ? tProject("projectsDashboard.managersPieChartCardHeaderForOneProjectWithMultipleManagers", {
            defaultValue: defaultTranslations.managersPieChartCardHeaderForOneProjectWithMultipleManagers,
            projectCount,
          })
        : tProject("projectsDashboard.managersPieChartCardHeader", {
            defaultValue: defaultTranslations.managersPieChartCardHeader,
            count: managerCount,
            projectCount,
          }),
    managersPieChartCardHeaderForOneProject: tProject("projectsDashboard.managersPieChartCardHeaderForOneProject", {
      defaultValue: defaultTranslations.managersPieChartCardHeaderForOneProject,
      count: managerCount,
    }),
    managersPieChartCardHeaderForOneProjectWithOneManager: tProject(
      "projectsDashboard.managersPieChartCardHeaderForOneProjectWithOneManager",
      {
        defaultValue: defaultTranslations.managersPieChartCardHeaderForOneProjectWithOneManager,
      }
    ),
    managersPieChartCardHeaderForOneProjectWithMultipleManagers: tProject(
      "projectsDashboard.managersPieChartCardHeaderForOneProjectWithMultipleManagers",
      {
        defaultValue: defaultTranslations.managersPieChartCardHeaderForOneProjectWithMultipleManagers,
        projectCount,
      }
    ),
    managersLabel: tProject("projectsDashboard.managersLabel", {
      defaultValue: defaultTranslations.managersLabel,
    }),
    manualCompletionBarTitle: tProject("projectsDashboard.manualCompletionBarTitle", {
      defaultValue: defaultTranslations.manualCompletionBarTitle,
    }),
    taskBasedCompletionBarTitle: tProject("projectsDashboard.taskBasedCompletionBarTitle", {
      defaultValue: defaultTranslations.taskBasedCompletionBarTitle,
    }),
    scheduleLabel: tProject("projectsDashboard.scheduleLabel", {
      defaultValue: defaultTranslations.scheduleLabel,
    }),
    onTimeProjectsLabel:
      onTimeProjectCount === 1
        ? tProject("projectsDashboard.onTimeProjectsLabelForOneProject", {
            defaultValue: defaultTranslations.onTimeProjectsLabelForOneProject,
          })
        : tProject("projectsDashboard.onTimeProjectsLabel", {
            defaultValue: defaultTranslations.onTimeProjectsLabel,
            count: onTimeProjectCount,
          }),
    onTimeProjectsLabelForOneProject: tProject("projectsDashboard.onTimeProjectsLabelForOneProject", {
      defaultValue: defaultTranslations.onTimeProjectsLabelForOneProject,
    }),
    lateProjectsLabel:
      lateProjectCount === 1
        ? tProject("projectsDashboard.lateProjectsLabelForOneProject", {
            defaultValue: defaultTranslations.lateProjectsLabelForOneProject,
          })
        : tProject("projectsDashboard.lateProjectsLabel", {
            defaultValue: defaultTranslations.lateProjectsLabel,
            count: lateProjectCount,
          }),
    lateProjectsLabelForOneProject: tProject("projectsDashboard.lateProjectsLabelForOneProject", {
      defaultValue: defaultTranslations.lateProjectsLabelForOneProject,
    }),
    noScheduleProjectsLabel:
      noScheduleProjectCount === 1
        ? tProject("projectsDashboard.noScheduleProjectsLabelForOneProject", {
            defaultValue: defaultTranslations.noScheduleProjectsLabelForOneProject,
          })
        : tProject("projectsDashboard.noScheduleProjectsLabel", {
            defaultValue: defaultTranslations.noScheduleProjectsLabel,
            count: noScheduleProjectCount,
          }),
    noScheduleProjectsLabelForOneProject: tProject("projectsDashboard.noScheduleProjectsLabelForOneProject", {
      defaultValue: defaultTranslations.noScheduleProjectsLabelForOneProject,
    }),
    pastStartProjectsLabel:
      pastStartProjectCount === 1
        ? tProject("projectsDashboard.pastStartProjectsLabelForOneProject", {
            defaultValue: defaultTranslations.pastStartProjectsLabelForOneProject,
          })
        : tProject("projectsDashboard.pastStartProjectsLabel", {
            defaultValue: defaultTranslations.pastStartProjectsLabel,
            count: pastStartProjectCount,
          }),
    pastStartProjectsLabelForOneProject: tProject("projectsDashboard.pastStartProjectsLabelForOneProject", {
      defaultValue: defaultTranslations.pastStartProjectsLabelForOneProject,
    }),
    pastEndProjectsLabel:
      pastEndProjectCount === 1
        ? tProject("projectsDashboard.pastEndProjectsLabelForOneProject", {
            defaultValue: defaultTranslations.pastEndProjectsLabelForOneProject,
          })
        : tProject("projectsDashboard.pastEndProjectsLabel", {
            defaultValue: defaultTranslations.pastEndProjectsLabel,
            count: pastEndProjectCount,
          }),
    pastEndProjectsLabelForOneProject: tProject("projectsDashboard.pastEndProjectsLabelForOneProject", {
      defaultValue: defaultTranslations.pastEndProjectsLabelForOneProject,
    }),
    customersFilterLabel: tProject("projectsDashboard.customersFilterLabel", {
      defaultValue: defaultTranslations.customersFilterLabel,
    }),
    membershipsFilterLabel: tProject("projectsDashboard.membershipsFilterLabel", {
      defaultValue: defaultTranslations.membershipsFilterLabel,
    }),
    suppliersFilterLabel: tProject("projectsDashboard.suppliersFilterLabel", {
      defaultValue: defaultTranslations.suppliersFilterLabel,
    }),
    workspacesFilterLabel: tProject("projectsDashboard.workspacesFilterLabel", {
      defaultValue: defaultTranslations.workspacesFilterLabel,
    }),
    appliedFiltersLabel: tProject("projectsDashboard.appliedFiltersLabel", {
      defaultValue: defaultTranslations.appliedFiltersLabel,
      filters,
    }),

    completedProjectsSectionHeader: tProject("projectsDashboard.completedProjectsSectionHeader", {
      defaultValue: defaultTranslations.completedProjectsSectionHeader,
    }),
    fromDatedPickerLabel: tProject("projectsDashboard.fromDatedPickerLabel", {
      defaultValue: defaultTranslations.fromDatedPickerLabel,
    }),
    toDatedPickerLabel: tProject("projectsDashboard.toDatedPickerLabel", {
      defaultValue: defaultTranslations.toDatedPickerLabel,
    }),
    unitLabel: tProject("projectsDashboard.unitLabel", {
      defaultValue: defaultTranslations.unitLabel,
    }),
    monthLabel: tProject("projectsDashboard.monthLabel", {
      defaultValue: defaultTranslations.monthLabel,
    }),
    weekLabel: tProject("projectsDashboard.weekLabel", {
      defaultValue: defaultTranslations.weekLabel,
    }),
    dayLabel: tProject("projectsDashboard.dayLabel", {
      defaultValue: defaultTranslations.dayLabel,
    }),
    numberOfProjectsLabel: tProject("projectsDashboard.numberOfProjectsLabel", {
      defaultValue: defaultTranslations.numberOfProjectsLabel,
    }),
    monthlyScaleLabel: tProject("projectsDashboard.monthlyScaleLabel", {
      defaultValue: defaultTranslations.monthlyScaleLabel,
    }),
    weeklyScaleLabel: tProject("projectsDashboard.weeklyScaleLabel", {
      defaultValue: defaultTranslations.weeklyScaleLabel,
    }),
    dailyScaleLabel: tProject("projectsDashboard.dailyScaleLabel", {
      defaultValue: defaultTranslations.dailyScaleLabel,
    }),
    completedBarCardHeader:
      completedProjectCount === 1
        ? tProject("projectsDashboard.completedBarCardHeaderForOneProject", {
            defaultValue: defaultTranslations.completedBarCardHeaderForOneProject,
          })
        : tProject("projectsDashboard.completedBarCardHeader", {
            defaultValue: defaultTranslations.completedBarCardHeader,
            count: completedProjectCount,
          }),
    completedBarCardHeaderForOneProject: tProject("projectsDashboard.completedBarCardHeaderForOneProject", {
      defaultValue: defaultTranslations.completedBarCardHeaderForOneProject,
    }),

    noData: tTreegrid("noData", { defaultValue: defaultTranslations.noData }),
    notStarted: tProject("projectStatusMenu.notStarted", { defaultValue: defaultTranslations.notStarted }),
    inProgress: tProject("projectStatusMenu.inProgress", { defaultValue: defaultTranslations.inProgress }),
    completed: tProject("projectStatusMenu.completed", { defaultValue: defaultTranslations.completed }),
    rejected: tProject("projectStatusMenu.rejected", { defaultValue: defaultTranslations.rejected }),
  }
}
